import { useCallback } from 'react';
import { useToast } from './useToast';

export enum ErrorCategory {
  AUTHENTICATION = 'auth_error',
  PERMISSION = 'permission_error',
  CONFIGURATION = 'config_error',
  GOOGLE_API = 'google_api_error',
  VALIDATION = 'validation_error',
  RATE_LIMIT = 'rate_limit_error',
  NETWORK = 'network_error',
  TIMEOUT = 'timeout_error',
  UNKNOWN = 'unknown_error'
}

interface ApiError {
  error?: string;
  message?: string;
  help?: string;
  details?: string;
  retry?: boolean;
  status?: number;
}

export function useErrorHandler() {
  const { success, error: errorToast, warning, info } = useToast();

  /**
   * Handles API errors and shows appropriate toast messages
   * Memoized with useCallback to prevent infinite loops in useEffect dependencies
   */
  const handleError = useCallback((error: unknown, fallbackMessage = 'An error occurred'): ApiError => {
    console.error('Error:', error);

    let errorMessage = fallbackMessage;
    let errorDetails = '';
    let errorType = ErrorCategory.UNKNOWN;
    let canRetry = false;

    // Structured API error with our standard format
    if (typeof error === 'object' && error !== null) {
      // If it has our standardized error format
      if ('error' in error && ('message' in error || 'details' in error || 'help' in error)) {
        const apiError = error as ApiError;
        errorType = apiError.error as ErrorCategory || ErrorCategory.UNKNOWN;
        errorMessage = apiError.message || fallbackMessage;
        errorDetails = apiError.details || apiError.help || '';
        canRetry = apiError.retry === true;
      }
      // Handle Supabase-specific error format
      else if ('code' in error && 'message' in error) {
        errorMessage = (error as { message: string }).message;
        if ('code' in error) {
          errorDetails = `Code: ${(error as { code: string }).code}`;
        }
      }
      // Plain Error object
      else if (error instanceof Error) {
        errorMessage = error.message;
        if (error.stack) {
          errorDetails = error.stack.split('\n')[0];
        }
      }
    }

    // Show toast with different styling based on error type
    const toastMessage = errorDetails
      ? `${errorMessage}. ${errorDetails}`
      : errorMessage;

    // Use different toast methods based on error type
    if (errorType === ErrorCategory.PERMISSION ||
        errorType === ErrorCategory.CONFIGURATION) {
      warning(toastMessage, 5000);
    } else if (errorType === ErrorCategory.RATE_LIMIT) {
      info(toastMessage, 5000);
    } else {
      errorToast(toastMessage, 5000);
    }

    return {
      error: errorType,
      message: errorMessage,
      details: errorDetails,
      retry: canRetry
    };
  }, [errorToast, warning, info]); // Dependencies: all toast functions used inside

  return { handleError };
}

/**
 * Parses error response from an Edge Function or API
 */
export function parseErrorResponse(response: any): ApiError {
  if (!response) {
    return {
      error: ErrorCategory.UNKNOWN,
      message: 'Unknown error (no response)',
    };
  }
  
  // Direct error message string
  if (typeof response === 'string') {
    return {
      error: ErrorCategory.UNKNOWN,
      message: response,
    };
  }
  
  // Standard error format
  if (response.error || response.message) {
    return {
      error: response.error || ErrorCategory.UNKNOWN,
      message: response.message || 'An error occurred',
      help: response.help || '',
      details: response.details || '',
      retry: response.retry || false,
      status: response.status
    };
  }
  
  // Supabase error format
  if (response.code && response.message) {
    return {
      error: `supabase_${response.code}`,
      message: response.message,
      details: `Code: ${response.code}`,
    };
  }
  
  // Unknown format, try to stringify
  try {
    const details = JSON.stringify(response);
    return {
      error: ErrorCategory.UNKNOWN,
      message: 'Unknown error format',
      details: details.length > 100 ? details.substring(0, 100) + '...' : details
    };
  } catch {
    return {
      error: ErrorCategory.UNKNOWN,
      message: 'Unknown error (cannot parse)',
    };
  }
}